import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { testimonials, stats } from '../data/mock';

const TestimonialsSection = () => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <section id="testimonials" className="py-24 lg:py-32 bg-black">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-24"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center p-6 rounded-lg border border-white/10 bg-white/5"
            >
              <span className="block text-4xl lg:text-5xl font-bold text-cyan-400 mb-2">
                {stat.value}
              </span>
              <span className="text-white/60 font-mono text-sm tracking-wider">
                {stat.label}
              </span>
            </motion.div>
          ))}
        </motion.div>

        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="service-button bg-pink-500/20 text-pink-400 border-pink-500/30 mb-4 inline-block">
            Testimonials
          </span>
          <h2 className="section-heading text-white mt-4">
            What Our Clients
            <span className="text-pink-400"> Say</span>
          </h2>
          <p className="body-large text-white/60 mt-4 max-w-2xl mx-auto">
            Don&apos;t just take our word for it. Hear directly from our satisfied clients.
          </p>
        </motion.div>

        {/* Video Testimonial */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className="relative rounded-xl overflow-hidden bg-white/5 border border-white/10">
            <video
              ref={videoRef}
              src={testimonials[0].videoUrl}
              className="w-full aspect-video object-cover"
              muted={isMuted}
              playsInline
              loop
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />

            {/* Video Controls Overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              {!isPlaying && (
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handlePlayPause}
                  className="w-20 h-20 rounded-full bg-cyan-500 text-black flex items-center justify-center shadow-lg shadow-cyan-500/30"
                >
                  <Play size={32} fill="currentColor" className="ml-1" />
                </motion.button>
              )}
            </div>

            {/* Bottom Controls */}
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
              <div className="flex items-center justify-between">
                <button
                  onClick={handlePlayPause}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                >
                  {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                </button>
                <button
                  onClick={handleMuteToggle}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                >
                  {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                </button>
              </div>
            </div>
          </div>

          <p className="text-center text-white/50 mt-4 font-mono text-sm">
            Click to play client testimonial video
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
